const STORAGE_KEY = 'ProwlResumeSnapshot';
const MAX_AGE_MS = 24 * 60 * 60 * 1000; // a day-old resume card stops being "a fact from before the page loaded" and starts being stale

export interface ProwlResumeSnapshot {
  categoryName: string | null;
  city: string | null;
  providerCount: number;
  savedAt: number;
}

/**
 * design-spec 3d: "returning mid-conversation" is a fact from before this
 * page load, rendered with zero animation. This app is client-rendered, so
 * there's no server to hand that fact to the first paint — localStorage is
 * the stand-in for that server-rendered state: read synchronously in a
 * useState initializer so the card is present on the very first frame
 * instead of popping in after a fetch (which would itself read as motion).
 */
export const saveResumeSnapshot = (
  snapshot: Omit<ProwlResumeSnapshot, 'savedAt'>
): void => {
  try {
    const value: ProwlResumeSnapshot = { ...snapshot, savedAt: Date.now() };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // localStorage full or unavailable — resume card is best-effort
  }
};

export const loadResumeSnapshot = (): ProwlResumeSnapshot | null => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ProwlResumeSnapshot;
    if (typeof parsed?.savedAt !== 'number') return null;
    if (Date.now() - parsed.savedAt > MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
};

export const clearResumeSnapshot = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // best-effort
  }
};
